import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check.svg";
import { useDebouncedCallback } from "../../../hooks/useDebounce";

const CustomSelect = ({
  options = [],
  width = "100%",
  height = "52px",
  placeholder = "Select an option",
  required = false,
  disabled = false,
  onChange = () => {},
  value = null,
  error = "",
  isMulti = false,
  searchable = false,
  onSearch,
  searchDebounceMs = 400,
  infinite = false,
  loadMore,
  hasMore = false,
  loadingMore = false,
  onDropdownOpen,

  wrapperClassName = "",
  controlClassName = "",
  dropdownClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const hasError = Boolean(error);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // Tính toán vị trí dropdown
  const calcDropdownPos = useCallback(() => {
    const anchor = controlRef.current;
    const dropdown = dropdownRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const vh = window.innerHeight;
    const gap = 4;

    // Lấy chiều cao thực tế của dropdown (nếu đã render)
    const dropdownHeight = dropdown ? dropdown.offsetHeight : 256;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;

    const placeAbove =
      spaceBelow < dropdownHeight + gap && spaceAbove > spaceBelow;

    const top = placeAbove
      ? Math.max(gap, rect.top - dropdownHeight - gap)
      : rect.bottom + gap;

    const left = rect.left;
    const w = rect.width;

    setDropdownPos({ top, left, width: w, placeAbove });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    calcDropdownPos();

    const timer = setTimeout(() => {
      calcDropdownPos();
    }, 10);

    return () => clearTimeout(timer);
  }, [isOpen, calcDropdownPos, options.length]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calcDropdownPos();
    });

    resizeObserver.observe(dropdownRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, calcDropdownPos]);

  useEffect(() => {
    if (!isOpen) return;

    const onResize = () => calcDropdownPos();
    const onScroll = () => calcDropdownPos();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, calcDropdownPos]);

  /* ================= Scroll infinite ================= */
  const handleScroll = () => {
    if (!infinite || loadingMore || !hasMore) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      loadMore?.();
    }
  };

  /* ================= Sync value ================= */
  useEffect(() => {
    if (isMulti) {
      setSelectedOptions(Array.isArray(value) ? value : []);
    } else {
      if (value) {
        const matched = options.find((o) => o.value === value.value);
        setSelectedOption(matched || value);
      } else {
        setSelectedOption(null);
      }
    }
  }, [value, options, isMulti]);

  /* ================= Click outside ================= */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const inControl = containerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inControl && !inDropdown) {
        setIsOpen(false);
        setSearchValue("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  /* ================= Focus search ================= */
  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchable]);

  /* ================= Logic ================= */
  const handleOptionClick = (option) => {
    if (isMulti) {
      const exists = selectedOptions.some((o) => o.value === option.value);
      const newSelected = exists
        ? selectedOptions.filter((o) => o.value !== option.value)
        : [...selectedOptions, option];

      setSelectedOptions(newSelected);
      onChange(newSelected);
    } else {
      setSelectedOption(option);
      onChange(option);
      setIsOpen(false);
    }
    setSearchValue("");
  };

  const executeSearch = useCallback(
    (nextSearchValue, immediate) => {
      const normalizedValue = String(nextSearchValue ?? "").trim();
      onSearch?.(normalizedValue, {
        immediate,
        search_value: normalizedValue,
      });
    },
    [onSearch],
  );
  const {
    run: runDebouncedSearch,
    flush: flushSearch,
    cancel: cancelSearch,
  } = useDebouncedCallback(executeSearch, searchDebounceMs);

  useEffect(() => {
    if (!isOpen) cancelSearch();
  }, [cancelSearch, isOpen]);

  const isOptionSelected = (option) =>
    isMulti
      ? selectedOptions.some((o) => o.value === option.value)
      : selectedOption?.value === option.value;

  const handleContainerClick = () => {
    if (disabled) return;
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen) onDropdownOpen?.();
  };

  /* ================= Render ================= */
  const dropdownNode =
    isOpen && mounted ? (
      <div
        ref={dropdownRef}
        className={`fixed z-[9999] bg-white border border-gray-300 shadow-lg ${
          dropdownClassName || "rounded-md"
        }`}
        style={{
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}
      >
        {searchable && (
          <div className="p-2 border-b">
            <input
              ref={searchInputRef}
              className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500"
              placeholder="Tìm kiếm..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                runDebouncedSearch(e.target.value, false);
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                e.stopPropagation();
                flushSearch(e.currentTarget.value, true);
              }}
            />
          </div>
        )}

        <div
          ref={listRef}
          onScroll={handleScroll}
          className="max-h-60 overflow-auto"
        >
          {options.length ? (
            options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleOptionClick(option)}
                className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-blue-50 ${
                  isOptionSelected(option) ? "bg-blue-100" : ""
                }`}
              >
                {isMulti && (
                  <div
                    className={`w-4 h-4 border rounded flex items-center justify-center ${
                      isOptionSelected(option)
                        ? "bg-blue-600 border-blue-600"
                        : "border-gray-300"
                    }`}
                  >
                    {isOptionSelected(option) && (
                      <CheckIcon className="w-2 h-2 text-white" />
                    )}
                  </div>
                )}
                {option.label}
              </div>
            ))
          ) : (
            <div className="p-3 text-sm text-gray-500 text-center">
              Không có dữ liệu
            </div>
          )}
        </div>

        {loadingMore && (
          <div className="p-2 text-xs text-center text-gray-500">
            Đang tải thêm...
          </div>
        )}
      </div>
    ) : null;

  return (
    <div
      ref={containerRef}
      className={`relative ${wrapperClassName} ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
      style={{ width }}
    >
      {/* Control */}
      <div
        ref={controlRef}
        onClick={handleContainerClick}
        style={{ height }}
        className={`
          flex items-center justify-between cursor-pointer px-3
          border bg-white box-border
          ${
            isOpen
              ? "border-blue-500 border-2"
              : hasError
                ? "border-red-500"
                : "border-gray-300"
          }
          ${controlClassName || "rounded-md"}
        `}
      >
        <div className="flex-1 flex flex-wrap gap-1 items-center text-gray-700">
          {isMulti ? (
            selectedOptions.length ? (
              selectedOptions.map((opt) => (
                <span
                  key={opt.value}
                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {opt.label}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOptions((prev) =>
                        prev.filter((o) => o.value !== opt.value),
                      );
                    }}
                    className="font-bold"
                  >
                    ×
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-400">
                {placeholder}
                {required && <span className="text-red-500 ml-1">*</span>}
              </span>
            )
          ) : selectedOption ? (
            selectedOption.label
          ) : (
            <span className="text-gray-400">
              {placeholder}
              {required && <span className="text-red-500 ml-1">*</span>}
            </span>
          )}
        </div>

        <ArrowDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {hasError && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {mounted && dropdownNode
        ? createPortal(dropdownNode, document.body)
        : null}
    </div>
  );
};

export default CustomSelect;
